import React, {Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState} from "react";
import { useApiCall, useApiEndpoint } from "../../../api";
import { ConfigInterface } from "../../../config";
import {TeamSpace, TeamSpaceItem, TeamSpaceTemplate} from "../../../api/repositories/documentation-library-repository";
import { DocPageVersion } from "../components/DocumentPageHeader";
import { usePermissions } from "@jane/frontend-medewerker/src/resources/PermissionsHook";
import {getSubTeamsRecursively} from "@jane/frontend-medewerker/src/pages/learn/resolveSubTeams";

export interface DocumentationLibraryContextType {
  editable: boolean;
  setEditable: Dispatch<SetStateAction<boolean>>;

  // Templates
  templates: TeamSpaceTemplate[];
  isLoadingTemplates: boolean;
  onTemplatesUpdated: () => void | Promise<void>;

  // Spaces
  spaces: TeamSpace[];
  selectedSpace: string | null;
  setSelectedSpace: Dispatch<SetStateAction<string>>;
  isLoadingSpaces: boolean;
  onSpacesUpdated: () => void | Promise<void>;

  // Pages
  page: TeamSpaceItem | null;
  selectPage: (itemId: string) => void;
  deselectPage: () => void;
  onPageUpdated: (shouldNotify?: boolean) => void | Promise<void>;
  onCancelPageUpdate: () => void;
  onPageDeleted: (page: TeamSpaceItem) => void;
  onPageReverted: (version: DocPageVersion) => void;
  getPages: (pageId: string) => void;
  pages: TeamSpaceItem[];
  isLoadingPages: boolean;

  // Permissions
  canEdit: (space: TeamSpace) => boolean;
  canManage: (space: TeamSpace) => boolean;

  content: string | null;
  setContent: Dispatch<SetStateAction<string>>;
}

export const DocumentationLibraryContext = React.createContext<DocumentationLibraryContextType>(
  {} as DocumentationLibraryContextType,
);

export function DocumentationLibraryContextProvider(props: {
  children: React.ReactNode;
  config: ConfigInterface;
}): ReactNode {
  const { documentations, teams } = useApiCall(props.config);
  const {
    resource: teamSpacesResource,
    reload: reloadTeamSpaces,
    isLoading: isLoadingSpaces,
  } = useApiEndpoint(() => documentations.browseMyTeamSpaces());

  const {
    resource: templatesResource,
    reload: reloadTemplates,
    isLoading: isLoadingTemplates,
  } = useApiEndpoint(() => documentations.browseTemplates());

  const { resource: allTeams } = useApiEndpoint(() => teams.all());
  const { resource: teamsResource } = useApiEndpoint(() => teams.myTeams());

  const {canManageTeamSpaces } = usePermissions();

  const [editable, setEditable] = useState<boolean>(false);
  const [page, setPage] = useState<TeamSpaceItem | null>(null);
  const [pages, setPages] = useState<TeamSpaceItem[]>([]);
  const [isLoadingPages, setIsLoadingPages] = useState<boolean>(false);
  const [content, setContent] = useState<string | null>(null);
  const [selectedSpace, setSelectedSpace] = useState<string | null>(null);

  useEffect(() => {
    let url = "/samen-werken";

    if (selectedSpace) {
      url += `/space/${selectedSpace}`;

      if (page) {
        url += `/page/${page.id}`;
      }
    }

    window.history.replaceState({}, "", url);
  }, [page]);

  return (
    <DocumentationLibraryContext.Provider
      value={{
        // Editor
        content: content,
        setContent: setContent as Dispatch<SetStateAction<string>>, // It can support `null`, but it should not expose it.
        editable: editable,
        setEditable: setEditable,

        // Templates
        templates: templatesResource ?? [],
        onTemplatesUpdated: () => reloadTemplates(),
        isLoadingTemplates: isLoadingTemplates,

        // Spaces
        spaces: teamSpacesResource ?? [],
        onSpacesUpdated: () => reloadTeamSpaces(),
        isLoadingSpaces: isLoadingSpaces,
        selectedSpace: selectedSpace,
        setSelectedSpace: setSelectedSpace as Dispatch<SetStateAction<string>>,

        // Pages
        pages: pages,
        page: page,
        isLoadingPages: isLoadingPages,

        selectPage: async (itemId) => {
          setContent(null);

          const pageDetails = await documentations.readPage(itemId);
          setPage(pageDetails);
          const currentVersion = pageDetails.versions.find((pd) => pd.isCurrentVersion);
          if (currentVersion) {
            setContent(currentVersion.content);
          }
        },

        deselectPage: () => {
          setPage(null);
          setContent(null);
        },
        onPageUpdated: async (shouldNotify?: boolean) => {
          if (page === null || content === null) return;
          await documentations.updatePageContent(page.id, content, shouldNotify);
          reloadTeamSpaces();
        },
        onPageReverted: async (version) => {
          await setContent(null);
          await reloadTeamSpaces();
          await setContent(version.content);
        },
        onCancelPageUpdate: async () => {
          if (!page) return;
          await setContent(null);

          const currentVersion = page.versions.find((pd) => pd.isCurrentVersion);
          if (currentVersion) {
            setContent(currentVersion.content);
          }
        },
        onPageDeleted: async (page) => {
          await setContent(null);
          await setPage(null);
          setPages((prev) => prev.filter((p) => p.id !== page.id));
        },
        getPages: async (teamSpaceId: string) => {
          const teamSpacePages = await documentations.browsePagesForTeamSpace(teamSpaceId);
          setPages(teamSpacePages)
        },

        canEdit: (space: TeamSpace) => {
          if (!allTeams || !teamsResource) {
            return false;
          }

          if (teamsResource.some((team) => team.id === space.ons_team_id)) {
            return true;
          }

          return space.permissions.some((permission) => {
            const userTeam = teamsResource.find((team) => team.id === permission.onsTeamId);

            if (userTeam && permission.onsTeamId === userTeam.id) {
              return permission.type === "editor";
            }

            if (permission.includeSubteams) {
              const subteamIds = getSubTeamsRecursively(allTeams, permission.onsTeamId!, 20).map((team) => team.id);
              return (teamsResource.some((team) => subteamIds.includes(team.id)) && permission.type === 'editor');
            }

            return false;
          });
        },

        canManage: (space: TeamSpace) => {
          if (!canManageTeamSpaces) {
            return false;
          }

          if (!allTeams || !teamsResource) {
            return false;
          }

          if (teamsResource.some((team) => team.id === space.ons_team_id)) {
            return true;
          }

          return space.permissions.some((permission) => {
            const userTeam = teamsResource.find((team) => team.id === permission.onsTeamId);

            if (userTeam && permission.onsTeamId === userTeam.id) {
              return permission.type === "editor";
            }

            if (permission.includeSubteams) {
              const subteamIds = getSubTeamsRecursively(allTeams, permission.onsTeamId!, 20).map((team) => team.id);
              return teamsResource.some((team) => subteamIds.includes(team.id)
                // && permission.type === 'editor'
              );
            }

            return false;
          });
        },
      }}
    >
      {props.children}
    </DocumentationLibraryContext.Provider>
  );
}

export function useDocumentationLibraryContext(): DocumentationLibraryContextType {
  return useContext(DocumentationLibraryContext);
}
